import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { standaloneAlterationsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Scissors, ChevronRight, Zap } from 'lucide-react';
import { HelpTooltip } from '@/components/HelpTooltip';

const STATUS_COLORS = {
  received: { bg: 'rgba(37,99,235,0.08)', text: '#2563EB' },
  assessed: { bg: 'rgba(147,51,234,0.08)', text: '#9333EA' },
  in_progress: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  completed: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
  delivered: { bg: 'rgba(27,42,74,0.08)', text: '#1B2A4A' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
};
const STATUS_LABELS = {
  received: 'Received', assessed: 'Assessed', in_progress: 'In Progress',
  completed: 'Completed', delivered: 'Delivered', cancelled: 'Cancelled',
};
const PAYMENT_COLORS = {
  unpaid: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626' },
  partial: { bg: 'rgba(245,158,11,0.10)', text: '#D97706' },
  paid: { bg: 'rgba(16,185,129,0.08)', text: '#059669' },
};
const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama',
  waistcoat: 'Waistcoat', dress: 'Dress', other: 'Other',
};

export default function AlterationsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [alterations, setAlterations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const res = await standaloneAlterationsAPI.list(params);
      setAlterations(res.data.alterations || []);
      setTotal(res.data.total || 0);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchData();
  };

  if (loading) return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-48" />
      <Skeleton className="h-12 w-full" />
      {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="alterations-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Alterations</h1>
          <HelpTooltip text="Track garment alterations. Create new alteration requests and monitor their progress." testId="help-alterations" />
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-muted)' }}>Walk-in & external garment alteration jobs</p>
        </div>
        <Button className="gap-1.5 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
          onClick={() => navigate('/app/alterations/new')} data-testid="new-alteration-btn">
          <Plus className="w-4 h-4" /> New Alteration Job
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input placeholder="Search by number or name..." className="pl-9 h-9 text-sm rounded-lg"
            value={search} onChange={e => setSearch(e.target.value)} data-testid="alteration-search" />
        </form>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setLoading(true); }}>
          <SelectTrigger className="w-40 h-9 text-sm rounded-lg" data-testid="status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-xs px-2 py-1">{total} total</Badge>
      </div>

      {/* List */}
      {alterations.length === 0 ? (
        <Card className="rounded-xl border bg-white">
          <CardContent className="py-12 text-center">
            <Scissors className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--ink-muted)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--ink-muted)' }}>No alteration jobs found</p>
            <Button variant="outline" className="mt-3 gap-1 text-xs rounded-lg"
              onClick={() => navigate('/app/alterations/new')}>
              <Plus className="w-3 h-3" /> Create First Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {alterations.map(a => {
            const sc = STATUS_COLORS[a.status] || STATUS_COLORS.received;
            const pc = PAYMENT_COLORS[a.payment_status] || PAYMENT_COLORS.unpaid;
            return (
              <Link key={a.id} to={`/app/alterations/${a.id}`}
                className="block rounded-xl border bg-white hover:shadow-md p-4"
                style={{ transition: 'box-shadow 200ms', borderColor: a.is_express ? 'rgba(217,119,6,0.3)' : 'rgba(27,42,74,0.08)' }}
                data-testid={`alteration-row-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(124,58,237,0.08)' }}>
                      <Scissors className="w-4 h-4" style={{ color: '#7C3AED' }} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>{a.alteration_number}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded-md"
                          style={{ backgroundColor: sc.bg, color: sc.text }}>{STATUS_LABELS[a.status]}</Badge>
                        {a.is_express && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#D97706', color: '#fff' }}>
                            EXPRESS
                          </span>
                        )}
                      </div>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                        {a.client_name || a.walk_in_name || 'Walk-in'} &middot; {GARMENT_LABELS[a.garment_type] || a.garment_type}
                        {a.garment_brand && ` (${a.garment_brand})`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                        ₹{(a.customer_charge || 0).toLocaleString('en-IN')}
                      </p>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded-md"
                        style={{ backgroundColor: pc.bg, color: pc.text }}>
                        {a.payment_status === 'paid' ? 'Paid' : a.payment_status === 'partial' ? `₹${a.amount_paid} paid` : 'Unpaid'}
                      </Badge>
                    </div>
                    <ChevronRight className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
